import { ThirdPartyDraggable } from "@fullcalendar/interaction";
import { onKeyStroke, syncRefs, useMouseInElement } from "@vueuse/core";
import { addWeeks } from "date-fns";
import {
  computed,
  ComputedRef,
  reactive,
  Ref,
  ref,
  watch,
  type ComponentPublicInstance,
} from "vue";

import controller from "@ctrl/controller";
import type { Controller, EventId, TaskId, Week } from "@ctrl/controller-rpc";

import { unplanTask } from "@/task";

import {
  calendarDropZoneId,
  sidebarOverdueDropZoneId,
  sidebarTodayDropZoneId,
  sidebarUnplannedDropZoneId,
  sidebarUpcomingDropZoneId,
  sidebarWeekDropZoneId,
} from "../types";
import { createDraftTaskEventInput } from "./draft-event";

const { weekOfDate } = controller;

export const draggableTaskClassName = "task-inline";

const SIDEBAR_DROP_ZONE_IDS = new Set([
  sidebarTodayDropZoneId,
  sidebarWeekDropZoneId,
  sidebarUpcomingDropZoneId,
  sidebarUnplannedDropZoneId,
]);

function findSourceDropZoneId(source: HTMLElement): string | null {
  let el: HTMLElement | null = source;
  while (el) {
    if (el.id && SIDEBAR_DROP_ZONE_IDS.has(el.id)) {
      return el.id;
    }
    if (
      el.id !== "" &&
      el.classList.contains("itemslist") &&
      el.closest(".planner-footer-task-list")
    ) {
      return el.id;
    }
    el = el.parentElement;
  }
  return null;
}

export const useDraggable = (
  controller: Controller,
  appendTo: Ref<ComponentPublicInstance | undefined>,
  weekDates: Ref<Date[]>,
  week: ComputedRef<Week>,
  today: Ref<Date>,
  updateTask: (taskId: string, scheduled: Week | Date) => void
) => {
  const taskId = ref<string | null>(null);
  // Set when the drag started from an existing allocation on the calendar
  // (instead of a task in the sidebar): dropping on a scheduling zone then
  // also deletes the allocation, so the task reappears in the target list
  // instead of staying pinned to the calendar (`Allocated_in_future`).
  const allocationEventId = ref<EventId | null>(null);
  const draggableTaskClassSelector = "." + draggableTaskClassName;
  const options = ref({
    draggable: draggableTaskClassSelector,
    mirror: {
      constrainDimensions: true,
      appendTo,
    },
    distance: 5, // Only start drag after moving 5px
  });
  const isDraggingTask = ref(false);
  const isDraggingResizing = ref(false);
  const isDragCanceled = ref(false);
  const setDraggingTask = (
    isDragging: boolean,
    id?: TaskId,
    eventId?: EventId
  ) => {
    isDraggingTask.value = isDragging;
    if (id) taskId.value = id;
    if (eventId) allocationEventId.value = eventId;
  };
  // Whether the dragged task can be scheduled to a week (stamped on the
  // drag target as `data-can-plan-week`, like `data-can-allocate`, see
  // TaskInline): false for bound tasks. Calendar allocation drags
  // (`setDraggingTask`) always can — bound tasks cannot be allocated.
  const canPlanDraggedTaskToWeek = ref(true);
  const isOutsideSidebarTodayDropZone = ref(true);
  // Raw mouse tracking for the week-scheduling zones; the exposed values
  // below also refuse tasks that cannot be week-scheduled.
  const rawOutsideSidebarWeekDropZone = ref(true);
  const rawOutsideSidebarUpcomingDropZone = ref(true);
  // While a task that cannot be scheduled to a week is dragged, the "This
  // week" and "Upcoming" sections are not drop zones — they do not
  // highlight, and dropping there does nothing (it used to unplan the task).
  const isOutsideSidebarWeekDropZone = computed(
    () => rawOutsideSidebarWeekDropZone.value || !canPlanDraggedTaskToWeek.value
  );
  const isOutsideSidebarUpcomingDropZone = computed(
    () =>
      rawOutsideSidebarUpcomingDropZone.value || !canPlanDraggedTaskToWeek.value
  );
  const isOutsideSidebarUnplannedDropZone = ref(true);
  const isOutsideCalendarDropZone = ref(true);
  const isOutsideFooterDropZones = reactive<Ref<boolean>[]>([
    ref(true),
    ref(true),
    ref(true),
    ref(true),
    ref(true),
    ref(true),
    ref(true),
  ]);
  const sourceDropZoneId = ref<string | null>(null);
  const isDroppedInSourceZone = (): boolean => {
    const source = sourceDropZoneId.value;
    if (!source) return false;
    if (source === sidebarTodayDropZoneId) {
      return !isOutsideSidebarTodayDropZone.value;
    }
    if (source === sidebarWeekDropZoneId) {
      return !isOutsideSidebarWeekDropZone.value;
    }
    if (source === sidebarUpcomingDropZoneId) {
      return !isOutsideSidebarUpcomingDropZone.value;
    }
    if (source === sidebarUnplannedDropZoneId) {
      return !isOutsideSidebarUnplannedDropZone.value;
    }
    const footerIndex = Number(source);
    if (
      !Number.isNaN(footerIndex) &&
      footerIndex >= 0 &&
      footerIndex < isOutsideFooterDropZones.length
    ) {
      return !isOutsideFooterDropZones[footerIndex].value;
    }
    return false;
  };
  // Remove the allocation the drag started from (if any), as the agenda
  // does when an allocation is dropped on a task list: the task would
  // otherwise stay classified as allocated and never show up in the
  // sidebar section it was just dropped on.
  const deleteAllocation = () => {
    if (allocationEventId.value)
      void controller.event.delete(allocationEventId.value, false, "app");
  };
  const stopWatchDragging = watch(isDraggingTask, (v) => {
    isDraggingResizing.value = v;
    if (!v) {
      if (taskId.value) {
        if (!isDragCanceled.value) {
          if (!isDroppedInSourceZone()) {
            if (!isOutsideSidebarTodayDropZone.value) {
              deleteAllocation();
              updateTask(taskId.value, today.value);
            } else if (!isOutsideSidebarWeekDropZone.value) {
              deleteAllocation();
              updateTask(taskId.value, week.value);
            } else if (!isOutsideSidebarUpcomingDropZone.value) {
              deleteAllocation();
              const nextWeek = weekOfDate(addWeeks(new Date(), 1));
              updateTask(taskId.value, nextWeek);
            } else if (!isOutsideSidebarUnplannedDropZone.value) {
              deleteAllocation();
              void unplanTask(controller, taskId.value, "app");
            } else {
              isOutsideFooterDropZones.forEach((isOutsideDropZone, i) => {
                if (!isOutsideDropZone.value && taskId.value) {
                  deleteAllocation();
                  updateTask(taskId.value, weekDates.value[i]);
                }
              });
            }
          }
        } else if (isOutsideCalendarDropZone.value) {
          isDragCanceled.value = false;
        }
        taskId.value = null;
        allocationEventId.value = null;
        sourceDropZoneId.value = null;
        canPlanDraggedTaskToWeek.value = true;
      }
    }
  });
  const start = () => {
    if (isMirrorCreated.value) {
      isDraggingTask.value = true;
    }
  };
  const stop = () => {
    isDraggingTask.value = false;
    isMirrorCreated.value = false;
  };
  const isMirrorCreated = ref(false);
  type MirrorEvent = { data: { mirror: HTMLElement; source: HTMLElement } };
  const mirrorCreated = (e: MirrorEvent) => {
    isMirrorCreated.value = true;
    e.data.mirror.classList.add("zTop");
    taskId.value = e.data.source.dataset.taskId ?? null;
    canPlanDraggedTaskToWeek.value =
      e.data.source.dataset.canPlanWeek !== "false";
    sourceDropZoneId.value = findSourceDropZoneId(e.data.source);
    // Sidebar drags carry no allocation.
    allocationEventId.value = null;
  };
  const mirrorMove = (e: MirrorEvent) => {
    if (isOutsideCalendarDropZone.value) {
      e.data.mirror.classList.remove("hidden");
    } else {
      // Hide mirror when over calendar
      e.data.mirror.classList.add("hidden");
    }
  };
  const instances = ref<Map<string, ThirdPartyDraggable>>(new Map());
  const watchers = ref<Map<string, { stopAll: () => void }>>(new Map());
  function setupDropZone(el: HTMLElement, elId: string): void;

  function setupDropZone(els: Array<HTMLElement>): void;

  function setupDropZone(
    el: HTMLElement | Array<HTMLElement>,
    elId?: string
  ): void {
    if (el instanceof HTMLElement) {
      if (!elId) throw new Error("elId is required when el is an HTMLElement");
      if (watchers.value.get(elId)) watchers.value.get(elId)?.stopAll();
      const { isOutside, stop } = useMouseInElement(el);
      const stopSyncRefs =
        elId === sidebarOverdueDropZoneId
          ? () => {} // No drop zone tracking for overdue section
          : syncRefs(
              isOutside,
              elId === sidebarTodayDropZoneId
                ? isOutsideSidebarTodayDropZone
                : elId === sidebarWeekDropZoneId
                  ? rawOutsideSidebarWeekDropZone
                  : elId === sidebarUpcomingDropZoneId
                    ? rawOutsideSidebarUpcomingDropZone
                    : elId === sidebarUnplannedDropZoneId
                      ? isOutsideSidebarUnplannedDropZone
                      : elId === calendarDropZoneId
                        ? isOutsideCalendarDropZone
                        : isOutsideFooterDropZones[Number(elId)]
            );
      watchers.value.set(elId, {
        stopAll: () => {
          stop();
          stopSyncRefs();
        },
      });
      if (elId !== calendarDropZoneId) {
        if (instances.value.get(elId)) instances.value.get(elId)?.destroy();
        const instance = new ThirdPartyDraggable(el, {
          itemSelector: draggableTaskClassSelector,
          eventData: function (taskEl) {
            const taskId = taskEl.dataset.taskId;
            if (!taskId) return null;
            const cleanup = onKeyStroke(
              "Escape",
              (e) => {
                e.preventDefault();
                if (isDraggingResizing.value) {
                  isDragCanceled.value = true;
                  const mouseUpEvent = new MouseEvent("mouseup", {
                    bubbles: true,
                    cancelable: true,
                  });
                  document.dispatchEvent(mouseUpEvent);
                }
                cleanup(); // This will unregister the listener
              },
              { dedupe: true }
            );
            return createDraftTaskEventInput(taskId, taskEl.innerText);
          },
        });
        instances.value.set(elId, instance);
      }
    } else if (Array.isArray(el)) {
      el.forEach((el, index) => {
        setupDropZone(el, index.toString());
      });
    } else throw new Error("Invalid argument");
  }
  const release = () => {
    stopWatchDragging();
    for (const { stopAll } of watchers.value.values()) stopAll();
    watchers.value.clear();
    for (const instance of instances.value.values()) instance.destroy();
    instances.value.clear();
  };

  return {
    isDragCanceled,
    isDraggingResizing,
    isDraggingTask,
    isOutsideFooterDropZones,
    isOutsideSidebarTodayDropZone,
    isOutsideSidebarWeekDropZone,
    isOutsideSidebarUpcomingDropZone,
    isOutsideSidebarUnplannedDropZone,
    mirrorCreated,
    mirrorMove,
    options,
    setDraggingTask,
    setupDropZone,
    start,
    stop,
    release,
  };
};
